/**
 * Sunset math and timezone helpers. Pure functions, no dependencies, safe on
 * both the server and the browser.
 *
 * Sunset uses the standard NOAA/"sunrise equation" solar model, accurate to
 * about a minute for our latitude — plenty for telling pilots when the light
 * goes.
 */

export const LAS_VEGAS = { lat: 36.1699, lon: -115.1398 } as const;
export const VENUE_TZ = "America/Los_Angeles";

const RAD = Math.PI / 180;

/** Julian day number for a calendar date (yyyy-mm-dd) at 00:00 UTC. */
function julianDay(dateISO: string): number {
  const ms = Date.parse(`${dateISO}T00:00:00Z`);
  if (Number.isNaN(ms)) throw new Error(`Bad date: ${dateISO}`);
  return ms / 86_400_000 + 2_440_587.5;
}

function julianToDate(julian: number): Date {
  return new Date((julian - 2_440_587.5) * 86_400_000);
}

/**
 * Sunset instant for a calendar day at a latitude/longitude. Returns null on
 * polar days with no sunset (never happens in Las Vegas, but the math can).
 */
export function sunsetUtc(
  dateISO: string,
  lat: number = LAS_VEGAS.lat,
  lon: number = LAS_VEGAS.lon,
): Date | null {
  const n = Math.round(julianDay(dateISO) - 2_451_545.0 + 0.0008);
  const meanSolarTime = n - lon / 360;
  const M = (357.5291 + 0.98560028 * meanSolarTime) % 360;
  const C =
    1.9148 * Math.sin(M * RAD) + 0.02 * Math.sin(2 * M * RAD) + 0.0003 * Math.sin(3 * M * RAD);
  const lambda = (M + C + 180 + 102.9372) % 360;
  const transit =
    2_451_545.0 + meanSolarTime + 0.0053 * Math.sin(M * RAD) - 0.0069 * Math.sin(2 * lambda * RAD);
  const sinDec = Math.sin(lambda * RAD) * Math.sin(23.44 * RAD);
  const cosDec = Math.cos(Math.asin(sinDec));
  const cosOmega =
    (Math.sin(-0.833 * RAD) - Math.sin(lat * RAD) * sinDec) / (Math.cos(lat * RAD) * cosDec);
  if (cosOmega > 1 || cosOmega < -1) return null;
  const omega = Math.acos(cosOmega) / RAD;
  return julianToDate(transit + omega / 360);
}

/** Minutes offset from UTC for a timezone at a given instant (e.g. -420). */
export function zoneOffsetMinutes(at: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(at);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? "0");
  const asUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour") % 24,
    get("minute"),
    get("second"),
  );
  return Math.round((asUtc - at.getTime()) / 60_000);
}

/**
 * A wall-clock time on a calendar day in a timezone, as a UTC instant.
 * Handles daylight saving by resolving the offset twice.
 */
export function zonedTimeToUtc(dateISO: string, timeHHMM: string, timeZone = VENUE_TZ): Date {
  const [h, m] = timeHHMM.split(":").map((v) => Number(v));
  const naive = Date.parse(`${dateISO}T00:00:00Z`) + ((h ?? 0) * 60 + (m ?? 0)) * 60_000;
  let guess = new Date(naive);
  for (let i = 0; i < 2; i += 1) {
    const offset = zoneOffsetMinutes(guess, timeZone);
    guess = new Date(naive - offset * 60_000);
  }
  return guess;
}

/** `18:42` — wall-clock time of an instant in a timezone. */
export function timeInZone(at: Date, timeZone = VENUE_TZ): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  }).format(at);
}

/** `6:42 PM` — friendly wall-clock time of an instant in a timezone. */
export function friendlyTimeInZone(at: Date, timeZone = VENUE_TZ): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
  }).format(at);
}

/** `2026-09-21` — calendar day of an instant in a timezone. */
export function dayInZone(at: Date, timeZone = VENUE_TZ): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(at);
}

/**
 * Start instant for one night. When `sunsetOffsetMinutes` is set the clock time
 * comes from that day's real sunset (negative = before sunset); otherwise the
 * fixed wall-clock time is used.
 */
export function occurrenceStart(opts: {
  dateISO: string;
  startLocalTime: string;
  sunsetOffsetMinutes?: number | null;
  timeZone?: string;
  lat?: number;
  lon?: number;
}): Date {
  const timeZone = opts.timeZone ?? VENUE_TZ;
  if (opts.sunsetOffsetMinutes === null || opts.sunsetOffsetMinutes === undefined) {
    return zonedTimeToUtc(opts.dateISO, opts.startLocalTime, timeZone);
  }
  const sunset = sunsetUtc(opts.dateISO, opts.lat, opts.lon);
  if (!sunset) return zonedTimeToUtc(opts.dateISO, opts.startLocalTime, timeZone);
  // Round to the nearest 5 minutes so published times look intentional.
  const shifted = sunset.getTime() + opts.sunsetOffsetMinutes * 60_000;
  return new Date(Math.round(shifted / 300_000) * 300_000);
}

/** "1 hour before sunset", "30 min after sunset", "at sunset". */
export function describeSunsetOffset(minutes: number): string {
  if (minutes === 0) return "at sunset";
  const abs = Math.abs(minutes);
  const when = minutes < 0 ? "before" : "after";
  if (abs % 60 === 0) {
    const hours = abs / 60;
    return `${hours} hour${hours === 1 ? "" : "s"} ${when} sunset`;
  }
  return `${abs} min ${when} sunset`;
}
